import {
    useState,
    useCallback,
} from 'react';

import { InteractionType } from '@azure/msal-browser';
import { useMsal, useMsalAuthentication } from "@azure/msal-react";
import { useNotificationAPI } from '../components/NotificationProvider';
import { HandleGenericErrors } from '../utils/errorUtil';

export enum ResultType {
    Json,
    Blob,
    None,
}

export interface executeFunction {
    (method: string, endpoint: string, data?: any | undefined, contentType?: string | undefined, resultIsJson?: ResultType | undefined): any;
}

const blankError = {
    errors: [],
    messages: [],
    criticals: [],
    warnings: []
};

/**
 * Custom hook to call a web API using bearer token obtained from MSAL
 * @param {RedirectRequest} msalRequest
 * @returns
 */
export const useFetchWithMsal = (msalRequest: any) => {
    const notificationAPI = useNotificationAPI();
    const { instance } = useMsal();
    const [isLoading, setIsLoading] = useState(false);
    const [error, _setError] = useState(blankError as any);
    const setError = (_error: any) => {
        const genericErrors = HandleGenericErrors(_error, notificationAPI.addNotifications);
        _setError(genericErrors);
    }
    const [data, setData] = useState(null);

    const { result, error: msalError } = useMsalAuthentication(InteractionType.Redirect, {
        ...msalRequest,
        account: instance.getActiveAccount(),
        redirectUri: '/redirect'
    });

    /**
     * Execute a fetch request with the given options
     * @param {string} method: GET, POST, PUT, DELETE
     * @param {String} endpoint: The endpoint to call
     * @param {Object} data: The data to send to the endpoint, if any
     * @returns JSON response
     */
    const execute = (async (method: string, endpoint: string, data = undefined as any, contentType = 'application/json', resultIsJson = ResultType.Json) => {
        if (msalError) {
            return;
        }

        setIsLoading(true);

        if (result) {
            let response = null;
            try {

                const headers = new Headers();
                const bearer = `Bearer ${result.accessToken}`;
                headers.append("Authorization", bearer);

                headers.append('Content-Type', contentType);

                let options = {
                    method: method,
                    headers: headers,
                    body: data ? JSON.stringify(data) : null,
                };

                response = (await fetch(endpoint, options));

                let responseResult;
                if (response.status === 403) {
                    setError({ type: 'Disallowed', status: 403 });
                    setIsLoading(false);
                    return null;
                } else if (resultIsJson === ResultType.Json) {
                    responseResult = await response.json();
                } else if (resultIsJson === ResultType.Blob) {
                    responseResult = response.blob();
                }

                if (response.status === 200) {
                    setData(responseResult);
                    if (responseResult?.notifications) {
                        setError(responseResult.notifications);
                    }
                } else {
                    responseResult.status = response.status;
                    setError(responseResult);
                    responseResult = undefined;
                }

                setIsLoading(false);
                return responseResult;
            } catch (e) {
                setError(e);
                setIsLoading(false);
                throw e;
            }
        }
    }) as executeFunction;

    return {
        isLoading,
        error,
        data,
        execute: useCallback(execute, [result, msalError]), // to avoid infinite calls when inside a `useEffect`
    };
};

export const SingleRequest = async (accessToken: string, method: string, endpoint: string, data: any, addNotifications: Function, contentType = 'application/json', resultIsJson = ResultType.Json) => {
    let apiResult = null;
    let error = blankError as any;
    const setError = (_error: any) => {
        const genericErrors = HandleGenericErrors(_error, addNotifications);
        error = genericErrors;
    }

    if (!accessToken) {
        return;
    }

    let response = null;
    try {
        const headers = new Headers();
        const bearer = `Bearer ${accessToken}`;
        headers.append("Authorization", bearer);

        headers.append('Content-Type', contentType);

        let options = {
            method: method,
            headers: headers,
            body: data ? JSON.stringify(data) : null,
        };

        response = (await fetch(endpoint, options));

        let responseResult;
        if (response.status === 403) {
            setError({ type: 'Disallowed', status: 403 });
            return null;
        } else if (resultIsJson === ResultType.Json) {
            responseResult = await response.json();
        } else if (resultIsJson === ResultType.Blob) {
            responseResult = response.blob();
        }

        if (response.status === 200) {
            apiResult = responseResult;
            if (responseResult?.notifications) {
                setError(responseResult.notifications);
            }
        } else {
            if (responseResult.status) {
                responseResult.status = response.status;
                setError(responseResult);
                responseResult = undefined;
            } else {
                responseResult.status = 500;
                setError(responseResult);
                responseResult = undefined;
            }
        }

    } catch (e) {
        setError(e);
        throw e;
    }

    return { apiResult, error };
}